/* ====== Common Media Gallery File, Used in both RCFJ & Bespoke product's Image Gallery ====== */
if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.ifMediaStack = this.hasAttribute("data-media-stack");

        const currentVariantMedia = this.querySelector(".is-current-variant-media");
        this.lastVariantMediaId = parseInt(currentVariantMedia?.dataset.mediaId);

        this.isVariantChange = false; // Track whether the slide change is from a variant switch

        if (!this.thumbnails) return;
        this.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((thumbnail) => {
            thumbnail.addEventListener(
              "click",
              this.onThumbnailClick.bind(this),
            );
          });

        this.addEventListener( "sliderChanged", this.onSlideChanged.bind(this) );
        // Set initial active slide focus
        requestAnimationFrame(() => {
          const activeSlide = this.viewer?.querySelector( ".is-current-variant-media", );
          if (activeSlide) {
            this.updateSlideFocus(activeSlide);
          }
        });
      }
      get viewer() {
        return this.querySelector('[id^="Gallery-Viewer"]');
      }
      get thumbnails() {
        return this.querySelector('[id^="Gallery-Thumbnail-Slider"]');
      }

      /* ===== Update focusability for active/inactive slides ===== */
      updateSlideFocus(activeSlide) {
      if (!activeSlide || !this.viewer) return;

      this.viewer.querySelectorAll("[data-media-id]").forEach((slide) => {
        const isActive = slide === activeSlide;

        slide.querySelectorAll( ".rc-pswp-trigger, .rc-video-play-button" ).forEach((element) => {
          element.setAttribute("tabindex", isActive ? "0" : "-1");
        });
      });
    }

      /* ===== Handle slide changed event — sync thumbnail position ===== */
      onSlideChanged(event) {
        if (!event || !event.detail) return;

        const currentElement = event.detail.currentElement;
        const mediaId = currentElement?.dataset?.mediaId;

        if (!mediaId) return;
        // Set tabindex after slider has changed
        this.updateSlideFocus(currentElement);
        if (!this.thumbnails) return;
        const thumbnail = this.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );

        if (!thumbnail) return;
        if (
          !thumbnail
            .querySelector(".thumbnail-media") .hasAttribute("aria-current")
        ) {
          this.setActiveThumbnail(thumbnail);
        }
        if (this.thumbnails.initSliderStatus)
          this.thumbnails.moveElementToOptimalPosition(thumbnail);
      }
      /* ===== Handle thumbnail click ===== */
      onThumbnailClick(event) {
        const thumbnail = event.currentTarget;
        this.setActiveThumbnail(thumbnail);
        this.isVariantChange = true; // User-initiated click — use smooth animation
        this.changeMedia(thumbnail.getAttribute("data-target"));
      }
      /* ===== Update the gallery when variant changes. :: If hiding other variant images, reinstalls the slider. ===== */
      updateGallery(currentVariantMediaId) {
        if (
          currentVariantMediaId === this.lastVariantMediaId ||
          currentVariantMediaId == null
        )
          return;
        this.lastVariantMediaId = currentVariantMediaId;

        // Update current variant media classes
        this.viewer
          .querySelectorAll(".is-current-variant-media")
          .forEach((element) =>
            element.classList.remove("is-current-variant-media"),
          );
        this.viewer
          .querySelectorAll(`[data-media-id="${currentVariantMediaId}"]`)
          .forEach((element) =>
            element.classList.add("is-current-variant-media"),
          );

        if (this.thumbnails) {
          this.thumbnails
            .querySelector(".is-current-variant-thumbnail")
            ?.classList.remove("is-current-variant-thumbnail");
          this.thumbnails
            .querySelector(`[data-target="${currentVariantMediaId}"]`)
            ?.classList.add("is-current-variant-thumbnail");
        }
        // If hide-variants is enabled, also update media groups
        if (this.hasAttribute("data-hide-variants")) {
          const currentMedia = this.viewer.querySelector(
            `[data-media-id="${currentVariantMediaId}"]`,
          );

          const groupName = currentMedia?.dataset.mediaGroup;

          this.viewer
            .querySelectorAll(".is-current-variant-media-group")
            .forEach((element) =>
              element.classList.remove("is-current-variant-media-group"),
            );
          if (groupName) {
            this.viewer
              .querySelectorAll(`[data-media-group='${groupName}']`).forEach((element) => {
                element.classList.add("is-current-variant-media-group");
              });
          }
          if (this.thumbnails) {
            this.thumbnails
              .querySelectorAll(".is-current-variant-thumbnail-group")
              .forEach((element) =>
                element.classList.remove("is-current-variant-thumbnail-group"),
              );
            if (groupName) {
              this.thumbnails
                .querySelectorAll(`[data-thumbnail-group='${groupName}']`)
                .forEach((element) => { element.classList.add("is-current-variant-thumbnail-group");
              });
            }
          }
          // Reinstall slider when hide-variants is active
          if (!this.ifMediaStack || webvista.isMobileScreen()) {
            this.viewer.reInstall();
            if (this.thumbnails) this.thumbnails.reInstall();
          }
        }
        this.isVariantChange = true; // Variant switch — jump instantly, no animation
        this.changeMedia(currentVariantMediaId);
        // On mobile, scroll page back to top of gallery on variant change
        // if (webvista.isMobileScreen()) {
        //   webvista.scrollElementToHeaderBottom(this, 16);
        // }
      }
      /* ===== Switch the active media in the gallery.
       * Uses silent (instant) mode for variant changes to prevent sliding animation.
       * Uses animated mode for user thumbnail clicks. ===== */
      changeMedia(mediaId) {
        const activeMedia = this.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;
        if (this.ifMediaStack) {
          // Stack layout — move the featured media to the front
          activeMedia.parentElement.prepend(activeMedia);
        } else {
          if (!this.viewer.initSliderStatus) return;
          if (this.isVariantChange) {
            // silent=true: disables transition, jumps directly to slide, re-enables after TRANSITION_DURING ms
            // This prevents the slider from visibly animating through all slides on variant switch
            this.viewer.slideByElement(activeMedia, true);
            this.isVariantChange = false;
          } else {
            // Normal animated slide for user interactions
            this.viewer.slideByElement(activeMedia, false);
          }
        }
      }
      /* ===== Set the active thumbnail button state ===== */
      setActiveThumbnail(thumbnail) {
        if (!this.thumbnails || !thumbnail) return;
        this.thumbnails.querySelectorAll(".thumbnail-media") .forEach((element) => element.removeAttribute("aria-current"));

        thumbnail.querySelector("button").setAttribute("aria-current", true);
      }
    },
  );
}